import React, { useState, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Typography, CircularProgress, Button, Box, Tooltip, TextField, IconButton, MenuItem } from '@mui/material';
import { Download as DownloadIcon, Send as SendIcon, MoreHoriz as MoreIcon } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import DetallesTraduccionModal from '../molecules/DetallesTraduccionModal';
import { ModalGeneral } from '../../Componentes/ModalGeneral';
import { useTranslation } from '../translationContext';
import { useBody } from '../../Body/state/BodyContext';
import useMediaQuery from '@mui/material/useMediaQuery';

const TraduccionesTraductor = () => {
    const { getTranslationsByStatus, getTraductors, downloadTranslation, updateTranslationFile, updateTranslation } = useTranslation();
    const { setMessage, setSuccess } = useBody();
    const [loading, setLoading] = useState(true);
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState('');
    const [data, setData] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [traductors, setTraductors] = useState([]);
    const matches = useMediaQuery('(min-width:1185px)');

    const fetchTranslations = async () => {
        try {
            const translations = await getTranslationsByStatus(2);
            const initializedData = translations.map(translation => ({
                ...translation,
                selectedTraductor: null,
                selectedFile: null,
            }));
            const traductors = await getTraductors()
            setTraductors(traductors)
            setData(initializedData);
        } catch (error) {
            console.error('Error fetching translations:', error);
            setMessage('Error al cargar las traducciones pendientes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTranslations();
    }, [refresh]);

    const handleFileChange = (e, rowIndex) => {
        const file = e.target.files[0];

        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setMessage('El archivo excede el tamaño máximo permitido de 10 MB.');
                return;
            }

            if (file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
                setMessage('Solo se permiten archivos con extensión .docx.');
                return;
            }

            setData(prevData =>
                prevData.map((item, index) =>
                    index === rowIndex ? { ...item, selectedFile: file } : item
                )
            );
        }
    };

    const handleTraductorChange = (e, rowIndex) => {
        const selectedTraductor = traductors.find(t => t.id === e.target.value);

        setData(prevData =>
            prevData.map((item, index) =>
                index === rowIndex ? { ...item, selectedTraductor } : item
            )
        );
    };

    const handleDownloadFile = async (type, folder, fileName) => {
        try {
            const response = await downloadTranslation(type, folder, fileName);
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setSuccess('Archivo descargado correctamente');
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            setMessage('Error al descargar el archivo');
        }
    };

    const handleOpenDetailsModal = (row) => {
        setSelectedDetail(row);
        setOpenDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setOpenDetailsModal(false);
        setSelectedDetail('');
    };

    const handleSubmit = async (row) => {
        const { id, selectedFile, selectedTraductor } = row.original;

        if (!selectedFile || !selectedTraductor) {
            setMessage('Por favor, selecciona un traductor y un archivo.');
            return;
        }

        const translationData = {
            traductor_id: selectedTraductor.id,
            traductor_name: selectedTraductor.username,
            status_id: "4",
        };

        try {
            handleCloseDetailsModal();
            setLoading(true);
            const response = await updateTranslationFile(id, selectedFile);
            if (response?.success !== false) {
                await updateTranslation(translationData, id);
            }
            setSuccess('Traducción actualizada correctamente');
        } catch (error) {
            console.error('Error al enviar la traducción:', error);
            setMessage('Error al enviar la traducción');
        } finally {
            setRefresh(prev => !prev);
            setLoading(false);
        }
    };

    const columns = [
        {
            header: 'Detalles',
            size: matches ? 160 : 100,
            Cell: ({ cell }) => (
                <Button
                    sx={{ textTransform: 'none' }}
                    onClick={() => handleOpenDetailsModal(cell.row)}>
                    {matches ?
                        <VisibilityIcon sx={{ marginRight: "7px" }} />
                        :
                        <MoreIcon />
                    }
                    {matches ? "Ver Detalles" : ""}
                </Button>
            ),
        },
        {
            header: 'Título',
            accessorKey: 'title',
            size: 120,
            Cell: ({ cell }) => (
                <Tooltip title={cell.getValue()} placement="bottom-start">
                    <span style={{
                        display: 'block',
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '200px'
                    }}>
                        {cell.getValue()}
                    </span>
                </Tooltip>
            ),
        },
        {
            header: 'Documento original',
            accessorKey: 'file_original.file_name',
            size: 120,
            Cell: ({ cell }) => {
                const fileName = cell.getValue();
                const folderName = cell.row.original.file_original.folder_name;
                return fileName ? (
                    <Button
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadFile('original', folderName, fileName)}
                    >
                        Descargar
                    </Button>
                ) : "Sin archivo adjuntado";
            },
        },
        {
            header: 'Traductor asignado',
            accessorKey: 'selectedTraductor.id',
            Cell: ({ row }) => (
                <TextField
                    select
                    fullWidth
                    label="Seleccionar"
                    value={row.original.selectedTraductor?.id || ''}
                    onChange={(e) => handleTraductorChange(e, row.index)}
                    size="small"
                >
                    {traductors.map((traductor) => (
                        <MenuItem key={traductor.id} value={traductor.id}>
                            {traductor.username}
                        </MenuItem>
                    ))}
                </TextField>
            ),
        },
        {
            header: 'Subir documento',
            Cell: ({ row }) => (
                <>
                    <input
                        type="file"
                        name="adjunto"
                        onChange={(e) => handleFileChange(e, row.index)}
                        accept=".docx"
                    />
                </>
            ),
        },
        {
            header: 'Enviar',
            Cell: ({ row }) => (
                <IconButton
                    onClick={() => handleSubmit(row)}
                    disabled={!row.original.selectedFile || !row.original.selectedTraductor}
                >
                    <SendIcon sx={{ color: (!row.original.selectedFile || !row.original.selectedTraductor) ? '#ccc' : '#b71234' }} />
                </IconButton>
            ),
        },
    ];

    const responsiveColumns = columns.slice(0, -3);

    return (
        <>
            <Box sx={{ p: useMediaQuery('(min-width:600px)') ? 2 : 0 }} marginBottom={"10%"}>
                <Typography fontSize={matches ? "30px" : "28px"} my="40px" mx="10px">
                    Traducciones Pendientes
                </Typography>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : (<>
                    <MaterialReactTable
                        columns={matches ? columns : responsiveColumns}
                        enableColumnResizing={matches ? false : true}
                        enableFullScreenToggle={false}
                        data={data}
                        localization={MRT_Localization_ES}
                        enableRowSelection={false}
                        enablePagination={false}
                        enableSorting
                        initialState={{
                            density: 'compact',
                            columnVisibility: { id: false },
                        }}
                        layoutMode={"grid"}
                    />
                </>
                )}
            </Box>

            <ModalGeneral
                Contenido={<DetallesTraduccionModal
                    selectedDetail={selectedDetail}
                    autorizacion={false}
                    handleSubmit={handleSubmit}
                    traductors={traductors}
                />}
                open={openDetailsModal}
                onClose={handleCloseDetailsModal}
                titulo={"Detalles de la Traducción"}
                heightAuto
            />
        </>
    );
};

export default TraduccionesTraductor;
